import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import CardBase from "./cards/CardBase";
import useELTeams from "../hooks/queries/useELTeams";
import {
    Box,
    Typography,
    makeStyles,
    IconButton,
    Tooltip,
    Tab,
    Tabs,
} from "@material-ui/core";
import { AvatarGroup } from "@material-ui/lab";
import Avatar from "./Avatar";
import { Phone } from "@material-ui/icons";
import RequestCallbackDialog from "./RequestCallbackDialog";
import { RootState } from "../redux/store";
import TabPanel from "./TabPanel";

const useStyles = makeStyles((theme) => ({
    nameLabel: {
        marginTop: theme.spacing(1),
        textAlign: "center",
    },
    avatarBoxed: {
        border: "none",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        margin: `${theme.spacing(3)}px ${theme.spacing(4)}px`
    },
    avatarGroup: {
        flexWrap: "wrap",
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
    },
    tabs: {
        height: '30px',
        minHeight: '30px',
        '& button': {
            fontSize: '0.9em',
            height: '24px',
            minHeight: '24px',
            minWidth: 0,
        },
    },
}));

const ELTeamCard = () => {
    const classes = useStyles();

    const { selectedSite, sites } = useSelector(
        (state: RootState) => state.account
    );

    const accountExternalIds = selectedSite
        ? [selectedSite.externalId]
        : Object.keys(sites);

    const [isOpen, setIsOpen] = useState(false);
    
    const {
        data: teams,
        isLoading,
        error,
    } = useELTeams({ accountExternalIds });

    // A 401 from this endpoint means "no advice types / no team configured" rather
    // than a genuine auth failure, so treat it the same as an empty result and show
    // the friendly message instead of a red error alert. This also makes the deploy
    // order irrelevant (backend may still be returning 401). Real errors (5xx) still
    // surface via CardBase.
    const isNoTeamError = (error as any)?.status === 401;
    const fatalError = error && !isNoTeamError ? error : undefined;

    const displayTeams = useMemo(() => {
        let toDisplay = teams || [];

        toDisplay = toDisplay.filter(x => x.advisers.length > 0);

        return toDisplay;

    }, [teams]);

    const [currentTab, setCurrentTab] = useState(displayTeams[0]?.id);

    const currentTeam = useMemo(() => {
        if (!currentTab)
            return [];

        let team = displayTeams.find(x => x.id == currentTab);
        
        return team;

    }, [displayTeams, currentTab]);

    useEffect(() => {
        if (displayTeams.length)
            setCurrentTab(displayTeams[0].id);
    }, [displayTeams]);

    const title = useMemo(() => {

        if (displayTeams.length === 0)
            return "My Legal Team";

        if (displayTeams.length == 1)
            return `My ${displayTeams[0].name} Team`;

        if (!currentTab)
            return "Loading.."

        return (
            <Tabs className={classes.tabs} value={currentTab} onChange={(e, x) => setCurrentTab(x)} style={{ marginBottom: "10px" }}>
                {displayTeams.map(t => <Tab key={t.id} value={t.id} label={`My ${t.name} Team`} />)};
            </Tabs>
        );

    }, [teams, currentTab]);
    
    return (
        <>
            <CardBase
                title={title}
                isLoading={isLoading}
                error={fatalError}
                rightComponent={
                    <Tooltip
                        title={
                            selectedSite === null
                                ? "Select a site to request a callback"
                                : "Request a callback"
                        }
                    >
                        <span>
                            <IconButton
                                disabled={selectedSite === null}
                                onClick={() => setIsOpen(true)}
                            >
                                <Phone />
                            </IconButton>
                        </span>
                    </Tooltip>
                }
                fullHeight
            >
                {!isLoading && !fatalError && displayTeams.length === 0 && (
                    <Box className={classes.emptyState}>
                        <Typography variant="body2" color="textSecondary" align="center">
                            Your legal team hasn't been set up yet. Please contact your account administrator.
                        </Typography>
                    </Box>
                )}
                {displayTeams.map(t => (
                    <TabPanel key={t.id} value={currentTab} index={t.id} >
                        <AvatarGroup className={classes.avatarGroup} max={4}>
                            {t.advisers.map((a) => (
                                    <Box
                                        className={classes.avatarBoxed}
                                        key={a.userId}
                                    >
                                        <Avatar src={a.photo} alt={a.name} size="50px" />
                                        <Typography className={classes.nameLabel} variant="subtitle2">
                                            {a.name}
                                        </Typography>
                                    </Box>
                                ))}
                        </AvatarGroup>
                    </TabPanel>
                ))}		
                
            </CardBase>
            <RequestCallbackDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                advisers={currentTeam?.advisers || []}
                adviceTypeId={currentTeam?.adviceTypeId}
            />
        </>
    );
};

export default ELTeamCard;
